import EditorialOverviewTags from './editorial-overview-tags';
import EditorialOverviewGallery from './editorial-overview-gallery';
import EditorialOverviewHighlights from './editorial-overview-highlights';
import { cn } from '@fshr/shared-react';
import BevSafeText from '../bev-safe-text';
import type { EditorialOverviewProps } from './editorial-overview.types';
import { HeadingLevel } from '../rich-text-title';

const HEADING_LEVEL_CLASS: Record<HeadingLevel, string> = {
	h1: 'text-display1',
	h2: 'text-display2',
	h3: 'text-display3',
	h4: 'text-display3',
	h5: 'text-display3',
	h6: 'text-display3',
};

const BODY_COPY_MAX_LENGTH = 600;

function truncateBodyCopy(text: string): string {
	if (text.length <= BODY_COPY_MAX_LENGTH) return text;
	return text.slice(0, BODY_COPY_MAX_LENGTH).replace(/\s\S*$/, '') + '…';
}

export default function EditorialOverview({
	title,
	overviewTitleHeadingLevel = 'h2',
	tags,
	bodyCopy,
	highlights,
	images,
	className,
}: EditorialOverviewProps) {
	const formattedTitle = title || 'Overview';
	const truncatedBodyCopy = truncateBodyCopy(bodyCopy);

	const TitleTag = overviewTitleHeadingLevel;
	const titleClass = HEADING_LEVEL_CLASS[overviewTitleHeadingLevel] ?? 'text-display3';

	return (
		<section
			className={cn(
				'w-full px-150 md:px-400 lg:px-[90px] xl:px-[calc(var(--spacing-600)*2)]',
				className
			)}
		>
			<div className="mx-auto w-full max-w-[var(--wide-grid-container-width-lg,1260px)] xl:max-w-[var(--wide-grid-container-width-xl)]">
				{/* Desktop: 2-column layout (lg+) */}
				<div className="lg:flex lg:flex-row lg:gap-x-300">
					{/* Desktop left column - Text Block */}
					<div className="lg:min-w-0 lg:flex-1">
						<div className="flex flex-col gap-y-stack-l">
							{/* Title + Tags + Body */}
							<div className="flex flex-col gap-y-stack-m">
								<TitleTag className={`${titleClass} capitalize`}>
									<BevSafeText>{formattedTitle}</BevSafeText>
								</TitleTag>
								<EditorialOverviewTags tags={tags} />
							</div>
							<p className="hidden lg:block text-text-primary text-body2">
								<BevSafeText>{truncatedBodyCopy}</BevSafeText>
							</p>
						</div>
					</div>

					{/* Desktop right column - Image Gallery (hidden on mobile) */}
					<div className="hidden lg:block lg:min-w-0 lg:flex-1">
						<EditorialOverviewGallery images={images} />
					</div>
				</div>

				{/* Mobile: Image gallery placed between tags and description */}
				<div className="lg:hidden mt-stack-l">
					<EditorialOverviewGallery images={images} />
				</div>

				{/* Mobile: Body copy after gallery on mobile */}
				<div className="lg:hidden mt-stack-l">
					<p className="text-text-primary text-body2">
						<BevSafeText>{truncatedBodyCopy}</BevSafeText>
					</p>
				</div>

				{/* Highlights row below everything */}
				<div className="mt-stack-l lg:mt-stack-xl">
					<EditorialOverviewHighlights highlights={highlights} />
				</div>
			</div>
		</section>
	);
}
